import React, { useState, useRef, useEffect } from 'react';
import { sendMessageToBot } from '../services/chatService';

// ... imports remain the same

// Suggested Questions Data
const SUGGESTED_QUESTIONS = [
    { id: 'ben', text: 'What benefits do you offer?' },
    { id: 'app', text: '⁠How do I apply to serve?' },
    { id: 'req', text: '⁠What are the requirements to serve?' }
];


const ChatInterface = () => {
    // ... existing state
    const [sessionId] = useState(() => 'session_' + Math.random().toString(36).substr(2, 9));
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: "Hello! I'm the Peace Corps Assistant. How can I help you today?",
            sender: 'bot',
            timestamp: new Date().toISOString()
        }
    ]);
    const [inputText, setInputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current?.scrollIntoView) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]); // Scroll on loading state change too

    const processMessage = async (text) => {
        const userMessage = {
            id: Date.now(),
            text: text,
            sender: 'user',
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText('');
        setIsLoading(true);

        try {
            const botResponse = await sendMessageToBot(userMessage.text, sessionId);
            setMessages(prev => [...prev, { ...botResponse, id: Date.now() + 1 }]);
        } catch (error) {
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                text: "I'm having trouble connecting right now. Please try again.",
                sender: 'bot',
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!inputText.trim()) return;
        await processMessage(inputText);
    };

    const handleChipClick = async (question) => {
        if (isLoading) return;
        await processMessage(question);
    };

    return (
        <div style={styles.container}>
            <div style={styles.chatWindow}>
                <div style={styles.messagesContainer}>
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            style={{
                                ...styles.messageWrapper,
                                justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                            }}
                        >
                            <div style={{
                                ...styles.messageBubble,
                                backgroundColor: msg.sender === 'user' ? 'var(--pc-blue-secondary)' : 'var(--pc-gray-light)',
                                color: msg.sender === 'user' ? 'white' : 'var(--pc-text-dark)',
                                borderBottomRightRadius: msg.sender === 'user' ? '4px' : '18px',
                                borderBottomLeftRadius: msg.sender === 'bot' ? '4px' : '18px',
                            }}>
                                {/* Render newlines as line breaks for formatted bot responses */}
                                {msg.text && msg.text.split('\n').map((line, i) => {
                                    // Helper to format bold text
                                    const formatText = (text) => {
                                        const parts = text.split(/(\*\*.*?\*\*)/g);
                                        return parts.map((part, index) => {
                                            if (part.startsWith('**') && part.endsWith('**')) {
                                                return <strong key={index}>{part.slice(2, -2)}</strong>;
                                            }
                                            return part;
                                        });
                                    };

                                    return (
                                        <React.Fragment key={i}>
                                            {formatText(line)}
                                            {i !== msg.text.split('\n').length - 1 && <br />}
                                        </React.Fragment>
                                    );
                                })}

                                {/* Render Citations/Sources if available */}
                                {msg.urls && msg.urls.length > 0 && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold', marginBottom: '4px' }}>Related Links:</div>
                                        {msg.urls.map((url, idx) => (
                                            <div key={idx} style={{ marginBottom: '4px' }}>
                                                <a
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all', fontSize: '0.85rem' }}
                                                >
                                                    {url}
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div style={{ ...styles.messageWrapper, justifyContent: 'flex-start' }}>
                            <div style={{ ...styles.messageBubble, backgroundColor: 'var(--pc-gray-light)', color: 'var(--pc-text-dark)', fontStyle: 'italic' }}>
                                <span className="typing-dot">.</span><span className="typing-dot">.</span><span className="typing-dot">.</span>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Suggested Questions Area */}
                <div style={styles.chipsContainer}>
                    {SUGGESTED_QUESTIONS.map(q => (
                        <button
                            key={q.id}
                            style={styles.chip}
                            onClick={() => handleChipClick(q.text)}
                            disabled={isLoading}
                        >
                            {q.text}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSendMessage} style={styles.inputArea}>
                    <input
                        type="text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Type your question here..."
                        style={styles.input}
                        disabled={isLoading}
                    />
                    <button
                        type="submit"
                        style={{
                            ...styles.sendButton,
                            opacity: isLoading || !inputText.trim() ? 0.7 : 1,
                            cursor: isLoading || !inputText.trim() ? 'default' : 'pointer'
                        }}
                        disabled={isLoading || !inputText.trim()}
                    >
                        Send
                    </button>
                </form>
            </div>
        </div>
    );
};

// Update styles to include chips
const styles = {
    // ... existing styles
    container: {
        width: '100%',
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px 10px', // Reduced padding for mobile
        height: 'calc(100vh - 80px)', // Adjusted height calculation
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box', // Explicitly ensure box-sizing
    },
    chatWindow: {
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0, 32, 91, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
    },
    messagesContainer: {
        flex: 1,
        padding: '20px',
        overflowY: 'auto',
        backgroundColor: '#FAFAFA',
    },
    messageWrapper: {
        display: 'flex',
        marginBottom: '16px',
    },
    messageBubble: {
        maxWidth: '70%',
        padding: '12px 16px',
        borderRadius: '18px',
        borderTopLeftRadius: '18px',
        borderTopRightRadius: '18px',
        fontSize: '1rem',
        lineHeight: '1.5',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
        whiteSpace: 'pre-wrap', // Respect newlines in text
    },
    chipsContainer: {
        padding: '10px 20px',
        display: 'flex',
        gap: '10px',
        overflowX: 'auto',
        backgroundColor: 'white',
        borderTop: '1px solid #f0f0f0',
        whiteSpace: 'nowrap',
        // Hide scrollbar for cleaner look
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
    },
    chip: {
        backgroundColor: '#f0f4f8',
        color: 'var(--pc-blue-primary)',
        border: '1px solid #dce3eb',
        borderRadius: '16px',
        padding: '8px 16px',
        fontSize: '0.9rem',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontWeight: '500',
    },
    inputArea: {
        padding: '20px',
        backgroundColor: 'white',
        borderTop: '1px solid #f0f0f0',
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
    },
    input: {
        flex: 1,
        padding: '12px 16px',
        borderRadius: '24px',
        border: '2px solid #e0e0e0',
        fontSize: '1rem',
        fontFamily: 'inherit',
        outline: 'none',
        transition: 'border-color 0.2s',
    },
    sendButton: {
        backgroundColor: 'var(--pc-blue-primary)',
        color: 'white',
        border: 'none',
        borderRadius: '24px',
        padding: '12px 24px',
        fontSize: '1rem',
        fontWeight: '600',
        fontFamily: 'inherit',
        transition: 'background-color 0.2s',
    }
};

export default ChatInterface;
